"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AddCartItemInput, Cart } from "@/types/cart";
import { LatestCartRequest } from "@/lib/commerce/cartClient";
import {
  addItemToNativeCart,
  applyNativeShippingEstimate,
  buildApplyNativeCouponRequest,
  buildGetNativeCouponRequest,
  buildRemoveNativeCouponRequest,
  interpretNativeCouponResult,
  NATIVE_CART_COUPON_ERROR,
  nativeCouponAppliedMessage,
  buildGetNativeCartRequest,
  buildNativeCartShippingEstimateRequest,
  buildNativeShippingEstimate,
  buildRemoveNativeCartItemRequest,
  buildUpdateNativeCartItemRequest,
  findNativeCartItemByWooProductId,
  interpretNativeCartMutation,
  interpretNativeCartShippingEstimate,
  interpretNativeCartSnapshot,
  mapNativeCartView,
  NATIVE_CART_ADD_ERROR,
  NATIVE_CART_COUPONS_UNAVAILABLE,
  NATIVE_CART_LOAD_ERROR,
  NATIVE_CART_REMOVE_ERROR,
  NATIVE_CART_SHIPPING_AT_CHECKOUT,
  NATIVE_CART_SHIPPING_ERROR,
  NATIVE_CART_SHIPPING_SELECT_ERROR,
  NATIVE_CART_UPDATE_ERROR,
  nativeShippingEstimateFromCache,
  resolveNativeWooProductId,
  selectNativeShippingEstimateRate,
  type NativeCartOutcome,
  type NativeCartShippingEstimate,
  type NativeCartRequest,
  type NativeCartSend,
} from "@/lib/commerce/nativeCartClient";
import { pushEcommerceEvent } from "@/lib/analytics/gtm";
import {
  getCartShippingContextKey,
  readShippingCache,
} from "@/lib/commerce/shippingCalculator";
import {
  CartContext,
  type CartContextValue,
  type CartMutationResult,
} from "./cartContext";

// Adaptador do carrinho NATIVO (2026-10-13). Só fala com /api/cart/native*
// (via lib/commerce/nativeCartClient.ts) -- nunca com as rotas legadas do
// WooCommerce (carrinho, cupons, frete do carrinho, endereço do checkout).
// Ligado apenas quando app/layout.tsx decide backend="native".
//
// Frete no carrinho (2026-10-14): com `shippingEstimateEnabled` (frete do
// checkout nativo ligado no servidor) a calculadora do carrinho usa
// POST /api/cart/native/shipping-estimate -- mesma regra do checkout, só
// estimativa, nada gravado. A escolha de uma opção no carrinho é local (só
// o resumo); a seleção que vale acontece no checkout.

const MUTATION_TIMEOUT_MS = 15_000;

const sendNativeCartRequest: NativeCartSend = async (
  request: NativeCartRequest,
) => {
  const response = await fetch(request.url, {
    method: request.method,
    cache: "no-store",
    credentials: "same-origin",
    headers: request.body ? { "Content-Type": "application/json" } : undefined,
    body: request.body ? JSON.stringify(request.body) : undefined,
    signal: AbortSignal.timeout(MUTATION_TIMEOUT_MS),
  });
  const body: unknown = await response.json().catch(() => null);
  return { status: response.status, ok: response.ok, body };
};

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function readCachedEstimate(contextKey: string): NativeCartShippingEstimate | null {
  if (typeof window === "undefined") return null;
  try {
    return nativeShippingEstimateFromCache(
      readShippingCache(window.localStorage, contextKey),
      contextKey,
    );
  } catch {
    return null;
  }
}

export function NativeCartProvider({
  children,
  shippingEstimateEnabled = false,
  couponsEnabled = false,
}: {
  children: ReactNode;
  shippingEstimateEnabled?: boolean;
  // Cupons nativos (NATIVE_COUPONS=on, decidido no servidor). Desligado:
  // comportamento anterior (cupons indisponíveis).
  couponsEnabled?: boolean;
}) {
  const [cart, setCartState] = useState<Cart | null>(null);
  const [unavailableItemKeys, setUnavailableItemKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState("");
  const [pendingItemKey, setPendingItemKey] = useState<string | null>(null);
  const [isCheckoutUpdating, setIsCheckoutUpdating] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const latestRequest = useRef(new LatestCartRequest());
  const cartIdRef = useRef<string | null>(null);
  const cartRef = useRef<Cart | null>(null);
  const initialLoad = useRef<Promise<void> | null>(null);
  // Serializa a adição de itens: duas adições seguidas sem carrinho
  // criariam dois carrinhos (dois cookies de convidado) em paralelo.
  const addQueue = useRef<Promise<unknown>>(Promise.resolve());
  // Estimativa de frete vigente (vale só para o carrinho de `contextKey`).
  const estimateRef = useRef<NativeCartShippingEstimate | null>(null);
  // Sequência própria: a estimativa não pode invalidar (via latestRequest)
  // o resultado de uma mutação do carrinho em andamento.
  const estimateSequence = useRef(0);

  // Carrinho exibido = carrinho do servidor + estimativa ainda válida.
  const withEstimate = useCallback(
    (nextCart: Cart): Cart => {
      if (!shippingEstimateEnabled) return nextCart;
      const contextKey = getCartShippingContextKey(nextCart);
      if (estimateRef.current?.contextKey !== contextKey) {
        estimateRef.current = nextCart.items.length
          ? readCachedEstimate(contextKey)
          : null;
      }
      return applyNativeShippingEstimate(nextCart, estimateRef.current);
    },
    [shippingEstimateEnabled],
  );

  // Aplica um resultado bem-sucedido com visão. Devolve o Cart mapeado.
  const applyOutcome = useCallback(
    (outcome: NativeCartOutcome, requestId: number): Cart | undefined => {
      if (outcome.kind !== "cart") return undefined;
      if (outcome.cartId) cartIdRef.current = outcome.cartId;
      if (!outcome.view) return undefined;
      const mapped = mapNativeCartView(outcome.view);
      const displayed = withEstimate(mapped.cart);
      if (latestRequest.current.isLatest(requestId)) {
        cartRef.current = displayed;
        setCartState(displayed);
        setUnavailableItemKeys(mapped.unavailableItemKeys);
      }
      return displayed;
    },
    [withEstimate],
  );

  const loadSnapshot = useCallback(
    async (requestId: number): Promise<Cart | undefined> => {
      const outcome = interpretNativeCartSnapshot(
        await sendNativeCartRequest(buildGetNativeCartRequest()),
      );
      if (outcome.kind === "error") {
        if (latestRequest.current.isLatest(requestId)) setError(outcome.message);
        return undefined;
      }
      // GET sem carrinho ativo: esquece o id antigo (ex.: convertido).
      cartIdRef.current = outcome.cartId;
      return applyOutcome(outcome, requestId);
    },
    [applyOutcome],
  );

  useEffect(() => {
    let active = true;
    const requestId = latestRequest.current.start();

    async function loadCart() {
      try {
        await loadSnapshot(requestId);
      } catch {
        if (active && latestRequest.current.isLatest(requestId)) {
          setError(NATIVE_CART_LOAD_ERROR);
        }
      } finally {
        if (active && latestRequest.current.isLatest(requestId)) {
          setIsHydrated(true);
          setIsLoading(false);
        }
      }
    }

    initialLoad.current = loadCart();
    return () => {
      active = false;
    };
  }, [loadSnapshot]);

  const waitForInitialLoad = useCallback(async () => {
    await initialLoad.current?.catch(() => undefined);
  }, []);

  // Recarrega com GET depois de uma mutação cuja resposta veio sem visão.
  const refreshAfterMutation = useCallback(
    async (requestId: number): Promise<Cart | undefined> => {
      try {
        return await loadSnapshot(requestId);
      } catch {
        return undefined;
      }
    },
    [loadSnapshot],
  );

  const runAddItem = useCallback(
    async (input: number | AddCartItemInput, quantity: number) => {
      await waitForInitialLoad();
      const requestId = latestRequest.current.start();
      setIsLoading(true);
      setError("");

      const payload: AddCartItemInput & { quantity: number } =
        typeof input === "number"
          ? { productId: input, quantity }
          : { ...input, quantity: input.quantity ?? 1 };

      try {
        const outcome = await addItemToNativeCart({
          send: sendNativeCartRequest,
          cartId: cartIdRef.current,
          input: payload,
          newIdempotencyKey,
        });

        if (outcome.kind === "error") {
          if (latestRequest.current.isLatest(requestId)) setError(outcome.message);
          return { success: false, message: outcome.message };
        }

        const nextCart =
          applyOutcome(outcome, requestId) ??
          (await refreshAfterMutation(requestId));
        if (latestRequest.current.isLatest(requestId)) setIsOpen(true);

        // Mesmo ponto central do adaptador legado: todos os botões de
        // compra passam por addItem.
        const addedItem = nextCart
          ? findNativeCartItemByWooProductId(
              nextCart,
              resolveNativeWooProductId(payload),
            )
          : undefined;
        if (nextCart && addedItem) {
          pushEcommerceEvent("add_to_cart", {
            currency: nextCart.currencyCode,
            value: addedItem.price * payload.quantity,
            free_shipping: addedItem.freeShipping === true,
            items: [
              {
                item_id: addedItem.sku || String(addedItem.productId),
                content_id: String(addedItem.productId),
                item_name: addedItem.name,
                price: addedItem.price,
                quantity: payload.quantity,
                free_shipping: addedItem.freeShipping === true,
              },
            ],
          });
        }

        return { success: true, message: "Produto adicionado ao carrinho." };
      } catch {
        if (latestRequest.current.isLatest(requestId)) setError(NATIVE_CART_ADD_ERROR);
        return { success: false, message: NATIVE_CART_ADD_ERROR };
      } finally {
        if (latestRequest.current.isLatest(requestId)) setIsLoading(false);
      }
    },
    [applyOutcome, refreshAfterMutation, waitForInitialLoad],
  );

  const addItem = useCallback(
    (input: number | AddCartItemInput, quantity = 1) => {
      const run = addQueue.current.then(() => runAddItem(input, quantity));
      addQueue.current = run.catch(() => undefined);
      return run;
    },
    [runAddItem],
  );

  const updateItem = useCallback(
    async (key: string, quantity: number): Promise<CartMutationResult> => {
      await waitForInitialLoad();
      const requestId = latestRequest.current.start();
      const cartId = cartIdRef.current;
      if (!cartId) {
        await refreshAfterMutation(requestId);
        return { success: false, message: NATIVE_CART_UPDATE_ERROR };
      }
      setIsCheckoutUpdating(true);
      setPendingItemKey(key);
      setError("");

      try {
        const outcome = interpretNativeCartMutation(
          await sendNativeCartRequest(
            buildUpdateNativeCartItemRequest(cartId, key, quantity, newIdempotencyKey()),
          ),
          cartId,
          NATIVE_CART_UPDATE_ERROR,
        );
        if (outcome.kind === "error") {
          if (latestRequest.current.isLatest(requestId)) setError(outcome.message);
          await refreshAfterMutation(requestId);
          return { success: false, message: outcome.message };
        }
        const nextCart =
          applyOutcome(outcome, requestId) ??
          (await refreshAfterMutation(requestId));
        return { success: true, message: "Quantidade atualizada.", cart: nextCart };
      } catch {
        if (latestRequest.current.isLatest(requestId)) setError(NATIVE_CART_UPDATE_ERROR);
        return { success: false, message: NATIVE_CART_UPDATE_ERROR };
      } finally {
        if (latestRequest.current.isLatest(requestId)) {
          setIsCheckoutUpdating(false);
          setPendingItemKey(null);
        }
      }
    },
    [applyOutcome, refreshAfterMutation, waitForInitialLoad],
  );

  const removeItem = useCallback(
    async (key: string) => {
      await waitForInitialLoad();
      const requestId = latestRequest.current.start();
      const cartId = cartIdRef.current;
      if (!cartId) {
        await refreshAfterMutation(requestId);
        return { success: false, message: NATIVE_CART_REMOVE_ERROR };
      }
      setIsLoading(true);
      setPendingItemKey(key);
      setError("");

      try {
        const outcome = interpretNativeCartMutation(
          await sendNativeCartRequest(
            buildRemoveNativeCartItemRequest(cartId, key, newIdempotencyKey()),
          ),
          cartId,
          NATIVE_CART_REMOVE_ERROR,
        );
        if (outcome.kind === "error") {
          if (latestRequest.current.isLatest(requestId)) setError(outcome.message);
          await refreshAfterMutation(requestId);
          return { success: false, message: outcome.message };
        }
        if (!applyOutcome(outcome, requestId)) await refreshAfterMutation(requestId);
        return { success: true, message: "Produto removido do carrinho." };
      } catch {
        if (latestRequest.current.isLatest(requestId)) setError(NATIVE_CART_REMOVE_ERROR);
        return { success: false, message: NATIVE_CART_REMOVE_ERROR };
      } finally {
        if (latestRequest.current.isLatest(requestId)) {
          setIsLoading(false);
          setPendingItemKey(null);
        }
      }
    },
    [applyOutcome, refreshAfterMutation, waitForInitialLoad],
  );

  const refreshCart = useCallback(async () => {
    const requestId = latestRequest.current.start();
    try {
      await loadSnapshot(requestId);
    } catch {
      // Igual ao legado: falha aqui não é crítica; a próxima carga corrige.
    }
  }, [loadSnapshot]);

  // Endereço não passa pelo carrinho nativo (o checkout nativo coleta o
  // endereço). Nenhuma chamada ao WooCommerce aqui.
  const updateCustomerAddress = useCallback(
    async (): Promise<CartMutationResult> => ({
      success: false,
      message: NATIVE_CART_SHIPPING_AT_CHECKOUT,
    }),
    [],
  );

  // Escolha local de uma opção da estimativa: atualiza só o resumo.
  const selectShippingRate = useCallback(
    async (
      packageId: number | string,
      rateId: string,
    ): Promise<CartMutationResult> => {
      const current = cartRef.current;
      if (!shippingEstimateEnabled || !current) {
        return { success: false, message: NATIVE_CART_SHIPPING_AT_CHECKOUT };
      }
      const nextEstimate = selectNativeShippingEstimateRate(
        estimateRef.current,
        packageId,
        rateId,
      );
      if (!nextEstimate) {
        return { success: false, message: NATIVE_CART_SHIPPING_SELECT_ERROR };
      }
      estimateRef.current = nextEstimate;
      const nextCart = applyNativeShippingEstimate(current, nextEstimate);
      cartRef.current = nextCart;
      setCartState(nextCart);
      return {
        success: true,
        message: "Opção de entrega atualizada.",
        cart: nextCart,
      };
    },
    [shippingEstimateEnabled],
  );

  const calculateShippingPostcode = useCallback(
    async (
      postcode: string,
      signal?: AbortSignal,
    ): Promise<CartMutationResult> => {
      if (!shippingEstimateEnabled) {
        return {
          success: true,
          message: NATIVE_CART_SHIPPING_AT_CHECKOUT,
          cart: cartRef.current ?? undefined,
        };
      }
      await waitForInitialLoad();
      const sequence = ++estimateSequence.current;
      const requestedCart = cartRef.current;
      if (!requestedCart?.items.length) {
        return { success: false, message: NATIVE_CART_SHIPPING_ERROR };
      }
      const contextKey = getCartShippingContextKey(requestedCart);
      const request = buildNativeCartShippingEstimateRequest(postcode);
      setIsCheckoutUpdating(true);
      try {
        const response = await fetch(request.url, {
          method: request.method,
          cache: "no-store",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(request.body),
          signal,
        });
        const body: unknown = await response.json().catch(() => null);
        const outcome = interpretNativeCartShippingEstimate({
          status: response.status,
          ok: response.ok,
          body,
        });
        if (outcome.kind === "error") {
          return { success: false, message: outcome.message };
        }
        const estimate = buildNativeShippingEstimate(
          outcome,
          contextKey,
          estimateRef.current,
        );
        // Só vira a estimativa vigente se o carrinho não mudou enquanto
        // calculava (senão ela seria de outro conjunto de itens).
        const current = cartRef.current;
        if (
          sequence === estimateSequence.current &&
          current &&
          getCartShippingContextKey(current) === contextKey
        ) {
          estimateRef.current = estimate;
          const nextCart = applyNativeShippingEstimate(current, estimate);
          cartRef.current = nextCart;
          setCartState(nextCart);
          return {
            success: true,
            message: "Opções de frete atualizadas.",
            cart: nextCart,
          };
        }
        return {
          success: true,
          message: "Opções de frete atualizadas.",
          cart: applyNativeShippingEstimate(requestedCart, estimate),
        };
      } catch (requestError) {
        if (
          requestError instanceof Error &&
          requestError.name === "AbortError"
        ) {
          return { success: false, message: "", aborted: true };
        }
        return { success: false, message: NATIVE_CART_SHIPPING_ERROR };
      } finally {
        if (sequence === estimateSequence.current) setIsCheckoutUpdating(false);
      }
    },
    [shippingEstimateEnabled, waitForInitialLoad],
  );

  const couponsUnavailable = useCallback(
    async (): Promise<CartMutationResult> => ({
      success: false,
      message: NATIVE_CART_COUPONS_UNAVAILABLE,
    }),
    [],
  );

  // Cupom nativo: o servidor valida, grava e devolve o carrinho recalculado.
  // Funciona com o carrinho travado pelo checkout (o cupom não muda os itens).
  const mutateCoupon = useCallback(
    async (action: "apply" | "remove", code: string): Promise<CartMutationResult> => {
      await waitForInitialLoad();
      const requestId = latestRequest.current.start();
      setIsCheckoutUpdating(true);
      try {
        const outcome = interpretNativeCouponResult(
          await sendNativeCartRequest(
            action === "apply" ? buildApplyNativeCouponRequest(code) : buildRemoveNativeCouponRequest(),
          ),
        );
        if (outcome.kind === "error") return { success: false, message: outcome.message };
        const nextCart = applyOutcome(outcome, requestId);
        return {
          success: true,
          message: action === "apply" ? nativeCouponAppliedMessage(outcome.view) : "Cupom removido.",
          cart: nextCart,
        };
      } catch {
        return { success: false, message: NATIVE_CART_COUPON_ERROR };
      } finally {
        if (latestRequest.current.isLatest(requestId)) setIsCheckoutUpdating(false);
      }
    },
    [applyOutcome, waitForInitialLoad],
  );

  const applyNativeCoupon = useCallback((code: string) => mutateCoupon("apply", code), [mutateCoupon]);
  const removeNativeCoupon = useCallback(() => mutateCoupon("remove", ""), [mutateCoupon]);

  const refreshCoupon = useCallback(async () => {
    if (!couponsEnabled) return;
    const requestId = latestRequest.current.start();
    try {
      const outcome = interpretNativeCouponResult(await sendNativeCartRequest(buildGetNativeCouponRequest()));
      if (outcome.kind === "cart") applyOutcome(outcome, requestId);
    } catch {
      // Falha aqui não é crítica: a próxima leitura corrige.
    }
  }, [applyOutcome, couponsEnabled]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const value = useMemo<CartContextValue>(
    () => ({
      backend: "native",
      supportsCoupons: couponsEnabled,
      supportsCartShipping: shippingEstimateEnabled,
      unavailableItemKeys,
      cart,
      isHydrated,
      isLoading,
      isOpen,
      error,
      pendingItemKey,
      isCheckoutUpdating,
      addItem,
      updateItem,
      removeItem,
      updateCustomerAddress,
      selectShippingRate,
      calculateShippingPostcode,
      refreshCart,
      applyCoupon: couponsEnabled ? applyNativeCoupon : couponsUnavailable,
      removeCoupon: couponsEnabled ? removeNativeCoupon : couponsUnavailable,
      refreshCoupon,
      openCart,
      closeCart,
    }),
    [
      addItem,
      calculateShippingPostcode,
      cart,
      closeCart,
      couponsUnavailable,
      couponsEnabled,
      applyNativeCoupon,
      removeNativeCoupon,
      refreshCoupon,
      error,
      isCheckoutUpdating,
      isHydrated,
      isLoading,
      isOpen,
      openCart,
      pendingItemKey,
      refreshCart,
      removeItem,
      selectShippingRate,
      shippingEstimateEnabled,
      unavailableItemKeys,
      updateCustomerAddress,
      updateItem,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
